/**
 * @fileoverview Table Component
 * @author Derek Seo (dseo@yext.com)
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.table');

import {TableBody,
  CellLink} from '/ui/components/table/body/tablebody';
import {TableColgroup} from '/ui/components/table/colgroup/tablecolgroup';
import {getPropsWithDefaults,
  SortDirection,
  CheckboxState,
  getColumnsWithSortDirection,
  getSortedDataRows,
  toggleSortDirection,
  INTERNAL_COL_IDS} from '/ui/components/table/common';
import {tableControls} from '/ui/components/table/controls/tablecontrols';
import {TableHead} from '/ui/components/table/head/tablehead';
import {ColActionTypes,
  useColumnReducer,
  useSimpleSelection,
  useSelectionForPaginatedData,
  useSelectionForUnloadedData} from '/ui/components/table/hooks';
import * as Types from '/ui/components/table/types';
import {getLabelAttrs} from '/ui/lib/props';

import * as styles from '/ui/components/table/table.module.scss';

/**
 * @param {!Types.TableProps} props
 * @return {React.ReactElement}
 */
export function Table(props) {
  props = getPropsWithDefaults(props);
  const {
    columns,
    dataRows: propDataRows,
    label,
    rowCallToAction,
    emptyContent,
    pagination,
    filterBar,
    filterPanel,
    rowSelectionOptions,
    isLoading,
    getCellLoadingContents,
    noDataNumLoadingRows,
  } = props;

  // NOTE: the addition of rowHeader could cause unexpected behavior if the row header is one of the hidden columns,
  // a best practice should probably be to never hide the row header as that could cause confusion for screen readers,
  // additionally, it is likely that there should only be a single row header for a given row, however tables could be
  // complex and so it is not technically incorrect to have more than one. See editors draft for more info:
  // https://www.w3.org/WAI/EO/Drafts/tutorials/tables/scope/
  const displayedColumns = columns.filter(col => !col.hidden);

  if (rowCallToAction) {
    displayedColumns.push(/** @type {!Types.CallToActionColumn} */ ({
      id: INTERNAL_COL_IDS.ROW_CTA,
      label: '',
      isCallToAction: true,
      width: isLoading ? '125px' : undefined,
    }));
  }

  const hasSelect = !!rowSelectionOptions;
  let selectHeaderState;
  if (hasSelect) {
    displayedColumns.unshift(
      /** @type {!Types.SelectColumn} */({
        id: INTERNAL_COL_IDS.SELECT,
        label: '',
        isSelect: true,
      }),
    );

    const numSelectedOnPage = propDataRows
      .filter(({id}) => rowSelectionOptions.isSelected(id))
      .length;

    if (numSelectedOnPage === 0) {
      selectHeaderState = CheckboxState.UNSELECTED;
    } else if (numSelectedOnPage === propDataRows.length) {
      selectHeaderState = CheckboxState.SELECTED;
    } else {
      selectHeaderState = CheckboxState.INDETERMINATE;
    }
  }

  const {topControls, bottomControls} = tableControls(props);


  return (
    <div aria-busy={isLoading} className={styles.tableAndControlsContainer}>
      {topControls}
      {filterBar}
      <div className={styles.filterPanelContainer}>
        {filterPanel}
      </div>
      <div className={styles.htmlTableContainer}>
        <table
          className={`${styles.table} ${(filterBar || filterPanel) ? styles.tableWithFilters : ''}`}
          aria-live="polite"
          {...getLabelAttrs(label)}
        >
          <TableColgroup columns={displayedColumns} />
          <TableHead
            columns={displayedColumns}
            isLoading={isLoading}
            controlsEnabled={propDataRows.length > 0 && !isLoading}
            selectHeaderState={selectHeaderState}
            onSelectHeader={rowSelectionOptions && rowSelectionOptions.onSelectHeader}
          />
          <TableBody
            columns={displayedColumns}
            dataRows={propDataRows}
            emptyContent={emptyContent}
            pagination={pagination}
            rowCallToAction={rowCallToAction}
            isSelected={rowSelectionOptions && rowSelectionOptions.isSelected}
            isDisabled={rowSelectionOptions && rowSelectionOptions.isDisabled}
            onSelectRow={rowSelectionOptions && rowSelectionOptions.onSelectRow}
            isSingleSelect={rowSelectionOptions?.isSingleSelect}
            isLoading={isLoading}
            getCellLoadingContents={getCellLoadingContents}
            noDataNumLoadingRows={noDataNumLoadingRows}
          />
        </table>
      </div>
      {bottomControls}
    </div>
  );
}

Table.CellLink = CellLink;

export {
  Types,
  SortDirection,
  CheckboxState,
  getColumnsWithSortDirection,
  getSortedDataRows,
  toggleSortDirection,
  ColActionTypes,
  useColumnReducer,
  useSimpleSelection,
  useSelectionForPaginatedData,
  useSelectionForUnloadedData,
};
